/**
 * PermissionGate Component
 *
 * Conditionally renders children based on RBAC permissions.
 * Use this to hide UI elements (buttons, sections, nav items) from users
 * who don't have sufficient access.
 *
 * Usage:
 *   // Hide a button if user doesn't have operator access to billing
 *   <PermissionGate segment="billing" minLevel="operator">
 *     <Button onClick={handleCredit}>Issue Credit</Button>
 *   </PermissionGate>
 *
 *   // Show alternative content if access denied
 *   <PermissionGate
 *     segment="settings"
 *     minLevel="admin"
 *     fallback={<p>Contact admin for access</p>}
 *   >
 *     <AdminPanel />
 *   </PermissionGate>
 *
 *   // Multiple segments (user needs access to ALL)
 *   <PermissionGate segments={[['billing', 'admin'], ['settings', 'operator']]}>
 *     <FinanceAdminPanel />
 *   </PermissionGate>
 */

import React, { ReactNode } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { BusinessSegment, FunctionRole } from '../../types/rbac'

interface PermissionGateProps {
  /**
   * Single segment to check (use with minLevel)
   */
  segment?: BusinessSegment

  /**
   * Minimum access level required (default: 'viewer')
   */
  minLevel?: FunctionRole

  /**
   * Multiple segment/level pairs - user must have ALL permissions
   * Format: [['segment', 'minLevel'], ...]
   */
  segments?: [BusinessSegment, FunctionRole][]

  /**
   * Content to render if permission check passes
   */
  children: ReactNode

  /**
   * Content to render if permission check fails (default: null/nothing)
   */
  fallback?: ReactNode

  /**
   * If true, inverts the check (shows children when user DOESN'T have access)
   * Useful for showing "request access" UI
   */
  invert?: boolean
}

export function PermissionGate({
  segment,
  minLevel = 'viewer',
  segments,
  children,
  fallback = null,
  invert = false,
}: PermissionGateProps): React.ReactElement | null {
  const { hasAccess } = usePermissions()

  let hasPermission: boolean

  if (segments && segments.length > 0) {
    // Multiple segments - user must have ALL permissions
    hasPermission = segments.every(([seg, level]) => hasAccess(seg, level))
  } else if (segment) {
    // Single segment check
    hasPermission = hasAccess(segment, minLevel)
  } else {
    // No segment specified - allow by default
    hasPermission = true
  }

  // Apply inversion if needed
  const shouldRender = invert ? !hasPermission : hasPermission

  if (shouldRender) {
    return <>{children}</>
  }

  return <>{fallback}</>
}

/**
 * Higher-order component version of PermissionGate
 *
 * Usage:
 *   const ProtectedButton = withPermission(Button, 'billing', 'operator')
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  segment: BusinessSegment,
  minLevel: FunctionRole = 'viewer',
  FallbackComponent?: React.ComponentType<P>
): React.FC<P> {
  return function PermissionWrapped(props: P) {
    return (
      <PermissionGate
        segment={segment}
        minLevel={minLevel}
        fallback={FallbackComponent ? <FallbackComponent {...props} /> : null}
      >
        <Component {...props} />
      </PermissionGate>
    )
  }
}

export default PermissionGate
